import { TZDate, tzName } from '@date-fns/tz';

import {
  DAYS_PER_WEEK,
  HOURS_PER_DAY,
  MINUTES_PER_HOUR,
  MONTHS_IN_YEAR,
  ONE_DAY,
  ONE_HOUR,
  ONE_MINUTE,
} from '@air/utilities';

const MINUTES_PER_DAY = HOURS_PER_DAY * MINUTES_PER_HOUR;
const HOURS_ON_CLOCK = 12;
const DAYS_TO_SEARCH = 8;
const MONDAY_SEARCH_WEEKS = 3;
const MONTHS_TO_SEARCH = 48;
const WEEKDAY_SUNDAY_ALIAS = 7;
const JS_WEEKDAY_MAX = 6;
const JS_WEEKDAY_MONDAY = 1;
const JS_WEEKDAY_FRIDAY = 5;
const LOCAL_WEEKDAYS_MONDAY_THROUGH_FRIDAY = Array.from(
  { length: JS_WEEKDAY_FRIDAY - JS_WEEKDAY_MONDAY + 1 },
  (_, index) => JS_WEEKDAY_MONDAY + index,
);
const CRON_FIELD_COUNT = 5;
const TEEN_ORDINALS_START = 11;
const TEEN_ORDINALS_END = 13;
const ORDINAL_MOD = 100;
const MINUTE_FIELD_MAX = 59;
const HOUR_FIELD_MAX = 23;
const DAY_FIELD_MAX = 31;

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const MONTH_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const FIXED_TIME_ZONE_ABBREVIATIONS: Record<string, string> = {
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
  'America/New_York': 'ET',
  UTC: 'UTC',
  'Europe/London': 'UK',
};

const CRON_TZ_PREFIX = /^CRON_TZ=([^\s]+)\s+(.+)$/i;

const CRON_DESCRIPTORS: Record<string, string> = {
  '@daily': '0 0 * * *',
  '@every 1h': '0 * * * *',
  '@every 1m': '* * * * *',
  '@hourly': '0 * * * *',
  '@midnight': '0 0 * * *',
  '@monthly': '0 0 1 * *',
  '@weekly': '0 0 * * 0',
};

export type AutomationRepeat = 'minute' | '5min' | '15min' | '30min' | 'hour' | 'day' | 'weekdays' | 'week' | 'month';

const CRON_WEEKDAY_WEEKDAYS_RANGE = '1-5';

const AUTOMATION_REPEAT_INTERVAL_MINUTES: Partial<Record<AutomationRepeat, number>> = {
  '5min': 5,
  '15min': 15,
  '30min': 30,
  minute: 1,
};

export const isSubDailyAutomationRepeat = (repeat: AutomationRepeat) =>
  repeat === 'minute' || repeat === '5min' || repeat === '15min' || repeat === '30min' || repeat === 'hour';

export type AutomationSchedule = {
  dayOfMonth: number;
  hour: number;
  minute: number;
  repeat: AutomationRepeat;
  /** Local JS weekdays (0 = Sunday) when `repeat === 'week'`. */
  weekdays: number[];
};

type ZonedDateTime = {
  day: number;
  hour: number;
  minute: number;
  month: number;
  weekday: number;
  year: number;
};

type UtcSchedule = {
  dayOfMonth: number;
  hour: number;
  minute: number;
  repeat: AutomationRepeat;
  /** UTC cron weekday field (0 = Sunday) when `repeat === 'week'`. */
  weekdays: number[];
};

type CronNumber = number | '*';

const readZonedParts = (instant: Date, timeZone: string): ZonedDateTime => {
  const zoned = new TZDate(instant, timeZone);

  return {
    day: zoned.getDate(),
    hour: zoned.getHours(),
    minute: zoned.getMinutes(),
    month: zoned.getMonth() + 1,
    weekday: zoned.getDay(),
    year: zoned.getFullYear(),
  };
};

const zonedDateTimeToUtc = (parts: ZonedDateTime, timeZone: string) =>
  new TZDate(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, timeZone);

const nextLocalDay = (parts: ZonedDateTime, timeZone: string): ZonedDateTime => {
  const noon = zonedDateTimeToUtc({ ...parts, hour: HOURS_ON_CLOCK, minute: 0 }, timeZone);
  const following = readZonedParts(new Date(noon.getTime() + ONE_DAY + ONE_HOUR), timeZone);

  return { ...following, hour: 0, minute: 0 };
};

const upcomingLocalDays = (start: ZonedDateTime, timeZone: string, count: number): ZonedDateTime[] => {
  const days = [start];
  Array.from({ length: Math.max(count - 1, 0) }).forEach(() => {
    const previous = days[days.length - 1];

    if (previous) {
      days.push(nextLocalDay(previous, timeZone));
    }
  });
  return days;
};

export const formatAutomationScheduleClock = (hour: number, minute: number) => {
  const suffix = hour < HOURS_ON_CLOCK ? 'am' : 'pm';
  const hourOnClock = hour % HOURS_ON_CLOCK;
  const hourLabel = hourOnClock === 0 ? HOURS_ON_CLOCK : hourOnClock;
  const minuteLabel = String(minute).padStart(2, '0');

  return `${hourLabel}:${minuteLabel}${suffix}`;
};

export const formatAutomationTime12h = (hour: number, minute: number) => {
  const meridiem = hour < HOURS_ON_CLOCK ? 'AM' : 'PM';
  const hourOnClock = hour % HOURS_ON_CLOCK;
  const hourLabel = hourOnClock === 0 ? HOURS_ON_CLOCK : hourOnClock;
  const minuteLabel = String(minute).padStart(2, '0');

  return `${hourLabel}:${minuteLabel} ${meridiem}`;
};

const formatSidebarClock = formatAutomationTime12h;

export type AutomationSidebarStamp = {
  date: string;
  time: string;
};

export const formatAutomationSidebarStamp = (parts: ZonedDateTime): AutomationSidebarStamp => ({
  date: `${MONTH_FULL[parts.month - 1]} ${parts.day}, ${parts.year}`,
  time: formatSidebarClock(parts.hour, parts.minute),
});

export const formatAutomationSidebarStampAt = ({
  at,
  timeZone,
}: {
  at: string;
  timeZone: string;
}): AutomationSidebarStamp => formatAutomationSidebarStamp(readZonedParts(new Date(at), timeZone));

export const splitCronSchedule = (cronSchedule: string): { cronTimeZone: string | null; expression: string } => {
  const trimmed = cronSchedule.trim().replace(/\s+/g, ' ');
  const match = CRON_TZ_PREFIX.exec(trimmed);

  if (match?.[1] && match[2]) {
    return { cronTimeZone: match[1], expression: normalizeCron(match[2]) };
  }

  return { cronTimeZone: null, expression: normalizeCron(trimmed) };
};

export const getAutomationCronDisplayTimeZone = (cronSchedule: string, viewerTimeZone: string) =>
  splitCronSchedule(cronSchedule).cronTimeZone ?? viewerTimeZone;

const scheduleTimeZone = getAutomationCronDisplayTimeZone;

export const getAutomationNextRunZoned = ({
  cronSchedule,
  now,
  timeZone,
}: {
  cronSchedule: string;
  now: Date;
  timeZone: string;
}): ZonedDateTime | null => {
  const { cronTimeZone, expression } = splitCronSchedule(cronSchedule);

  if (cronTimeZone) {
    const local = parseLocalSchedule(expression);

    if (!local) {
      return null;
    }

    const next = nextZonedInstant(local, cronTimeZone, now);

    return next ? readZonedParts(next, cronTimeZone) : null;
  }

  const utc = parseUtcSchedule(expression);
  const next = utc ? nextUtcInstant(utc, now) : null;

  if (!next) {
    return null;
  }

  return readZonedParts(next, timeZone);
};

export const formatAutomationDayOrdinalSuffix = (day: number) => {
  const lastTwo = day % ORDINAL_MOD;

  if (lastTwo >= TEEN_ORDINALS_START && lastTwo <= TEEN_ORDINALS_END) {
    return 'th';
  }
  const lastDigit = day % 10;

  if (lastDigit === 1) {
    return 'st';
  }
  if (lastDigit === 2) {
    return 'nd';
  }
  // biome-ignore lint/style/noMagicNumbers: third-day ordinal
  if (lastDigit === 3) {
    return 'rd';
  }

  return 'th';
};

const formatAppointment = (parts: ZonedDateTime) =>
  `${WEEKDAY_SHORT[parts.weekday]}, ${MONTH_SHORT[parts.month - 1]} ${parts.day} at ${formatAutomationScheduleClock(parts.hour, parts.minute)}`;

const normalizeCronWeekday = (value: number) => (value === WEEKDAY_SUNDAY_ALIAS ? 0 : value);

export const normalizeAutomationWeekdays = (weekdays: number[]) =>
  [...new Set(weekdays.filter((day) => day >= 0 && day <= JS_WEEKDAY_MAX))].sort((left, right) => left - right);

const parseCronWeekdayList = (weekdayField: string): number[] | null => {
  if (weekdayField === '*' || weekdayField.includes('-')) {
    return null;
  }

  const days: number[] = [];

  for (const token of weekdayField.split(',')) {
    const parsed = parseCronNumber(token.trim(), WEEKDAY_SUNDAY_ALIAS);

    if (parsed === null || parsed === '*') {
      return null;
    }
    days.push(normalizeCronWeekday(parsed));
  }

  return days.length > 0 ? normalizeAutomationWeekdays(days) : null;
};

const emptyWeekdays = (): number[] => [];

const parseCronMinuteIntervalRepeat = (minuteField: string): AutomationRepeat | null => {
  const match = /^\*\/(\d+)$/.exec(minuteField);

  if (!match?.[1]) {
    return null;
  }
  const interval = Number(match[1]);

  if (interval === AUTOMATION_REPEAT_INTERVAL_MINUTES['5min']) {
    return '5min';
  }
  if (interval === AUTOMATION_REPEAT_INTERVAL_MINUTES['15min']) {
    return '15min';
  }
  if (interval === AUTOMATION_REPEAT_INTERVAL_MINUTES['30min']) {
    return '30min';
  }

  return null;
};

const parseCronNumber = (field: string, max: number): CronNumber | null => {
  if (field === '*') {
    return '*';
  }
  if (!/^\d+$/.test(field)) {
    return null;
  }
  const value = Number(field);

  if (value > max) {
    return null;
  }

  return value;
};

const normalizeCron = (cronSchedule: string) => {
  const trimmed = cronSchedule.trim().replace(/\s+/g, ' ');
  const descriptor = CRON_DESCRIPTORS[trimmed.toLowerCase()];

  return descriptor ?? trimmed;
};

const parseCronFields = (expression: string): UtcSchedule | null => {
  const fields = normalizeCron(expression).split(' ');

  if (fields.length !== CRON_FIELD_COUNT) {
    return null;
  }
  const [minuteField, hourField, dayField, monthField, weekdayField] = fields;

  if (!minuteField || !hourField || !dayField || monthField !== '*' || !weekdayField) {
    return null;
  }

  const minuteIntervalRepeat = parseCronMinuteIntervalRepeat(minuteField);

  if (minuteIntervalRepeat && hourField === '*' && dayField === '*' && weekdayField === '*') {
    return { dayOfMonth: 1, hour: 0, minute: 0, repeat: minuteIntervalRepeat, weekdays: emptyWeekdays() };
  }

  const weekdayIsWeekdaysRange = weekdayField === CRON_WEEKDAY_WEEKDAYS_RANGE;
  const weekDayList = weekdayIsWeekdaysRange ? null : parseCronWeekdayList(weekdayField);
  const minute = parseCronNumber(minuteField, MINUTE_FIELD_MAX);
  const hour = parseCronNumber(hourField, HOUR_FIELD_MAX);
  const day = parseCronNumber(dayField, DAY_FIELD_MAX);
  const weekdayIsStar = weekdayField === '*';

  if (minute === null || hour === null || day === null || day === 0) {
    return null;
  }
  if (minute === '*' && hour === '*' && day === '*' && weekdayIsStar) {
    return { dayOfMonth: 1, hour: 0, minute: 0, repeat: 'minute', weekdays: emptyWeekdays() };
  }
  if (minute !== '*' && hour === '*' && day === '*' && weekdayIsStar) {
    return { dayOfMonth: 1, hour: 0, minute: 0, repeat: 'hour', weekdays: emptyWeekdays() };
  }
  if (minute === '*' || hour === '*') {
    return null;
  }
  if (day === '*' && weekdayIsStar) {
    return { dayOfMonth: 1, hour, minute, repeat: 'day', weekdays: emptyWeekdays() };
  }
  if (day === '*' && weekdayIsWeekdaysRange) {
    return { dayOfMonth: 1, hour, minute, repeat: 'weekdays', weekdays: emptyWeekdays() };
  }
  if (day === '*' && weekDayList) {
    return { dayOfMonth: 1, hour, minute, repeat: 'week', weekdays: weekDayList };
  }
  if (day !== '*' && weekdayIsStar) {
    return { dayOfMonth: day, hour, minute, repeat: 'month', weekdays: emptyWeekdays() };
  }

  return null;
};

const parseUtcSchedule = (cronSchedule: string): UtcSchedule | null =>
  parseCronFields(splitCronSchedule(cronSchedule).expression);

const parseLocalSchedule = (expression: string): AutomationSchedule | null => {
  const parsed = parseCronFields(expression);

  if (!parsed) {
    return null;
  }

  return {
    dayOfMonth: parsed.dayOfMonth,
    hour: parsed.hour,
    minute: parsed.minute,
    repeat: parsed.repeat,
    weekdays: parsed.weekdays,
  };
};

const nextUtcIntervalInstant = (intervalMinutes: number, after: Date): Date => {
  if (intervalMinutes === 1) {
    const truncated = Date.UTC(
      after.getUTCFullYear(),
      after.getUTCMonth(),
      after.getUTCDate(),
      after.getUTCHours(),
      after.getUTCMinutes(),
    );

    return new Date(truncated > after.getTime() ? truncated : truncated + ONE_MINUTE);
  }
  if (intervalMinutes === MINUTES_PER_HOUR) {
    const truncated = Date.UTC(after.getUTCFullYear(), after.getUTCMonth(), after.getUTCDate(), after.getUTCHours(), 0);

    return new Date(truncated > after.getTime() ? truncated : truncated + ONE_HOUR);
  }

  const minuteOfDay = after.getUTCHours() * MINUTES_PER_HOUR + after.getUTCMinutes();
  const nextMinuteOfDay = Math.ceil((minuteOfDay + 1) / intervalMinutes) * intervalMinutes;
  const dayOffset = Math.floor(nextMinuteOfDay / MINUTES_PER_DAY);
  const minute = nextMinuteOfDay % MINUTES_PER_DAY;

  return new Date(
    Date.UTC(
      after.getUTCFullYear(),
      after.getUTCMonth(),
      after.getUTCDate() + dayOffset,
      Math.floor(minute / MINUTES_PER_HOUR),
      minute % MINUTES_PER_HOUR,
    ),
  );
};

const nextZonedIntervalInstant = (intervalMinutes: number, timeZone: string, after: Date): Date => {
  const utc = nextUtcIntervalInstant(intervalMinutes, after);
  const parts = readZonedParts(utc, timeZone);
  const candidate = zonedDateTimeToUtc(
    {
      day: parts.day,
      hour: parts.hour,
      minute: parts.minute,
      month: parts.month,
      weekday: parts.weekday,
      year: parts.year,
    },
    timeZone,
  );

  return new Date(candidate.getTime() > after.getTime() ? candidate.getTime() : utc);
};

const nextZonedInstant = (schedule: AutomationSchedule, timeZone: string, after: Date): Date | null => {
  const intervalMinutes = AUTOMATION_REPEAT_INTERVAL_MINUTES[schedule.repeat];

  if (intervalMinutes) {
    return nextZonedIntervalInstant(intervalMinutes, timeZone, after);
  }
  if (schedule.repeat === 'hour') {
    const parts = readZonedParts(after, timeZone);
    const candidate = zonedDateTimeToUtc({ ...parts, hour: parts.hour, minute: schedule.minute }, timeZone);

    return new Date(candidate.getTime() > after.getTime() ? candidate.getTime() : candidate.getTime() + ONE_HOUR);
  }

  const today = readZonedParts(after, timeZone);
  const searchLength = schedule.repeat === 'month' ? MONTHS_TO_SEARCH : DAYS_TO_SEARCH;
  const days =
    schedule.repeat === 'month'
      ? Array.from({ length: searchLength }, (_, index) => {
          const monthIndex = today.month - 1 + index;
          const year = today.year + Math.floor(monthIndex / MONTHS_IN_YEAR);
          const month = (monthIndex % MONTHS_IN_YEAR) + 1;
          const probe = zonedDateTimeToUtc(
            { day: 1, hour: HOURS_ON_CLOCK, minute: 0, month, weekday: 0, year },
            timeZone,
          );
          const probed = readZonedParts(new Date(probe.getTime() + (schedule.dayOfMonth - 1) * ONE_DAY), timeZone);

          return probed;
        })
      : upcomingLocalDays(today, timeZone, searchLength);

  const match = days
    .map((day) => ({ day, instant: localInstant(day, schedule, timeZone) }))
    .find(
      (candidate) =>
        candidate.instant.getTime() > after.getTime() &&
        matchesLocalSchedule(candidate.day, candidate.instant, schedule, timeZone),
    );

  return match?.instant ?? null;
};

const nextUtcInstant = (schedule: UtcSchedule, after: Date): Date | null => {
  const intervalMinutes = AUTOMATION_REPEAT_INTERVAL_MINUTES[schedule.repeat];

  if (intervalMinutes) {
    return nextUtcIntervalInstant(intervalMinutes, after);
  }
  if (schedule.repeat === 'hour') {
    const truncated = Date.UTC(
      after.getUTCFullYear(),
      after.getUTCMonth(),
      after.getUTCDate(),
      after.getUTCHours(),
      schedule.minute,
    );

    return new Date(truncated > after.getTime() ? truncated : truncated + ONE_HOUR);
  }

  const candidates = Array.from(
    { length: schedule.repeat === 'month' ? MONTHS_TO_SEARCH : DAYS_TO_SEARCH },
    (_, index) => {
      if (schedule.repeat === 'month') {
        const monthIndex = after.getUTCMonth() + index;
        const year = after.getUTCFullYear() + Math.floor(monthIndex / MONTHS_IN_YEAR);
        const month = monthIndex % MONTHS_IN_YEAR;
        const instant = new Date(Date.UTC(year, month, schedule.dayOfMonth, schedule.hour, schedule.minute));

        if (
          instant.getUTCFullYear() !== year ||
          instant.getUTCMonth() !== month ||
          instant.getUTCDate() !== schedule.dayOfMonth
        ) {
          return null;
        }

        return instant;
      }
      const instant = new Date(
        Date.UTC(
          after.getUTCFullYear(),
          after.getUTCMonth(),
          after.getUTCDate() + index,
          schedule.hour,
          schedule.minute,
        ),
      );

      if (schedule.repeat === 'weekdays' && !isWeekdayMondayThroughFriday(instant.getUTCDay())) {
        return null;
      }
      if (schedule.repeat === 'week' && !schedule.weekdays.includes(instant.getUTCDay())) {
        return null;
      }

      return instant;
    },
  );

  return candidates.find((instant) => instant !== null && instant.getTime() > after.getTime()) ?? null;
};

const localInstant = (day: ZonedDateTime, schedule: AutomationSchedule, timeZone: string) =>
  zonedDateTimeToUtc({ ...day, hour: schedule.hour, minute: schedule.minute }, timeZone);

const isWeekdayMondayThroughFriday = (weekday: number) => weekday >= JS_WEEKDAY_MONDAY && weekday <= JS_WEEKDAY_FRIDAY;

const matchesLocalSchedule = (day: ZonedDateTime, instant: Date, schedule: AutomationSchedule, timeZone: string) => {
  if (schedule.repeat === 'weekdays' && !isWeekdayMondayThroughFriday(day.weekday)) {
    return false;
  }
  if (schedule.repeat === 'week' && !schedule.weekdays.includes(day.weekday)) {
    return false;
  }
  if (schedule.repeat === 'month' && day.day !== schedule.dayOfMonth) {
    return false;
  }
  const zoned = readZonedParts(instant, timeZone);

  return zoned.year === day.year && zoned.month === day.month && zoned.day === day.day;
};

export const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const timeZoneAbbreviation = (timeZone: string, now: Date) => {
  if (Object.hasOwn(FIXED_TIME_ZONE_ABBREVIATIONS, timeZone)) {
    return FIXED_TIME_ZONE_ABBREVIATIONS[timeZone] ?? timeZone;
  }

  return tzName(timeZone, now, 'short');
};

export const parseAutomationSchedule = ({
  cronSchedule,
  now,
  timeZone,
}: {
  cronSchedule: string;
  now: Date;
  timeZone: string;
}): AutomationSchedule | null => {
  const { cronTimeZone, expression } = splitCronSchedule(cronSchedule);

  if (cronTimeZone) {
    return parseLocalSchedule(expression);
  }

  const utc = parseCronFields(expression);

  if (!utc) {
    return null;
  }
  if (
    utc.repeat === 'minute' ||
    utc.repeat === 'hour' ||
    utc.repeat === '5min' ||
    utc.repeat === '15min' ||
    utc.repeat === '30min'
  ) {
    return { ...utc, weekdays: emptyWeekdays() };
  }
  const weeklyWallClock =
    utc.repeat === 'week' || utc.repeat === 'weekdays' ? localWallClockFromUtcWeeklyCron(utc, now, timeZone) : null;

  const next = nextUtcInstant(utc, now);

  if (!next) {
    return null;
  }
  const zoned = readZonedParts(next, timeZone);
  const hour = weeklyWallClock?.hour ?? zoned.hour;
  const minute = weeklyWallClock?.minute ?? zoned.minute;

  if (utc.repeat === 'weekdays') {
    return {
      dayOfMonth: 1,
      hour,
      minute,
      repeat: 'weekdays',
      weekdays: emptyWeekdays(),
    };
  }

  if (utc.repeat === 'week') {
    const localWeekdays = utcCronWeekdaysToLocalWeekdays(
      { hour: utc.hour, minute: utc.minute, weekdays: utc.weekdays },
      now,
      timeZone,
    );

    if (isLocalWeekdaysMondayThroughFriday(localWeekdays)) {
      return {
        dayOfMonth: 1,
        hour,
        minute,
        repeat: 'weekdays',
        weekdays: emptyWeekdays(),
      };
    }

    return {
      dayOfMonth: 1,
      hour,
      minute,
      repeat: 'week',
      weekdays: localWeekdays,
    };
  }

  return {
    dayOfMonth: utc.repeat === 'month' ? zoned.day : 1,
    hour,
    minute,
    repeat: utc.repeat,
    weekdays: emptyWeekdays(),
  };
};

const instantBeforeAnchoredLocalWeek = (now: Date, timeZone: string): Date | null => {
  const today = readZonedParts(now, timeZone);
  const weekDays = zonedWeekDaysFromFirstMonday(today, timeZone);
  const monday = weekDays[0];

  if (!monday) {
    return null;
  }

  const weekStart = zonedDateTimeToUtc({ ...monday, hour: 0, minute: 0 }, timeZone);

  return new Date(weekStart.getTime() - 1);
};

const localWallClockFromUtcWeeklyCron = (
  utc: Pick<UtcSchedule, 'hour' | 'minute' | 'repeat' | 'weekdays'>,
  now: Date,
  timeZone: string,
): { hour: number; minute: number } | null => {
  const anchorAfter = instantBeforeAnchoredLocalWeek(now, timeZone);

  if (!anchorAfter) {
    return null;
  }

  const instant = nextUtcInstant(
    {
      dayOfMonth: 1,
      hour: utc.hour,
      minute: utc.minute,
      repeat: utc.repeat,
      weekdays: utc.weekdays,
    },
    anchorAfter,
  );

  if (!instant) {
    return null;
  }

  const zoned = readZonedParts(instant, timeZone);

  return { hour: zoned.hour, minute: zoned.minute };
};

const utcCronWeekdaysToLocalWeekdays = (
  utc: Pick<UtcSchedule, 'hour' | 'minute' | 'weekdays'>,
  now: Date,
  timeZone: string,
) => {
  const anchorAfter = instantBeforeAnchoredLocalWeek(now, timeZone);

  if (!anchorAfter) {
    return emptyWeekdays();
  }

  return normalizeAutomationWeekdays(
    utc.weekdays
      .map((utcWeekday) => {
        const probe = nextUtcInstant(
          { dayOfMonth: 1, hour: utc.hour, minute: utc.minute, repeat: 'week', weekdays: [utcWeekday] },
          anchorAfter,
        );

        return probe ? readZonedParts(probe, timeZone).weekday : null;
      })
      .filter((weekday): weekday is number => weekday !== null),
  );
};

/** One local calendar week (Mon–Sun) so every selected weekday shares the same DST offset. */
const zonedWeekDaysFromFirstMonday = (today: ZonedDateTime, timeZone: string): ZonedDateTime[] => {
  const horizon = upcomingLocalDays(today, timeZone, DAYS_PER_WEEK * MONDAY_SEARCH_WEEKS);
  const monday = horizon.find((day) => day.weekday === JS_WEEKDAY_MONDAY);

  if (!monday) {
    return [];
  }

  const week: ZonedDateTime[] = [monday];

  for (let index = 1; index < DAYS_PER_WEEK; index += 1) {
    const previous = week[index - 1];

    if (!previous) {
      break;
    }
    week.push(nextLocalDay(previous, timeZone));
  }

  return week;
};

const localWeekdayToUtcCronParts = (
  hour: number,
  minute: number,
  localWeekday: number,
  timeZone: string,
  weekDays: ZonedDateTime[],
) => {
  const day = weekDays.find((candidate) => candidate.weekday === localWeekday);

  if (!day) {
    return null;
  }
  const instant = zonedDateTimeToUtc({ ...day, hour, minute }, timeZone);

  return {
    utcDow: instant.getUTCDay(),
    utcHour: instant.getUTCHours(),
    utcMinute: instant.getUTCMinutes(),
  };
};

const weeklyLocalScheduleToUtcCron = (schedule: AutomationSchedule, timeZone: string, now: Date) => {
  const localWeekdays = normalizeAutomationWeekdays(schedule.weekdays);

  if (localWeekdays.length === 0) {
    return null;
  }

  const today = readZonedParts(now, timeZone);
  const weekDays = zonedWeekDaysFromFirstMonday(today, timeZone);

  if (weekDays.length !== DAYS_PER_WEEK) {
    return null;
  }

  const utcParts = localWeekdays.map((localWeekday) =>
    localWeekdayToUtcCronParts(schedule.hour, schedule.minute, localWeekday, timeZone, weekDays),
  );

  if (utcParts.includes(null)) {
    return null;
  }

  const definedUtcParts = utcParts.filter((part): part is NonNullable<typeof part> => part !== null);
  const first = definedUtcParts[0];

  if (!first) {
    return null;
  }

  const sameClock = definedUtcParts.every(
    (part) => part.utcHour === first.utcHour && part.utcMinute === first.utcMinute,
  );

  if (!sameClock) {
    return null;
  }

  const utcWeekdays = normalizeAutomationWeekdays(definedUtcParts.map((part) => part.utcDow));

  return `${first.utcMinute} ${first.utcHour} * * ${utcWeekdays.join(',')}`;
};

const isLocalWeekdaysMondayThroughFriday = (weekdays: number[]) => {
  const normalized = normalizeAutomationWeekdays(weekdays);

  return (
    normalized.length === LOCAL_WEEKDAYS_MONDAY_THROUGH_FRIDAY.length &&
    LOCAL_WEEKDAYS_MONDAY_THROUGH_FRIDAY.every((weekday, index) => normalized[index] === weekday)
  );
};

const weekdaysLocalScheduleToUtcCron = (schedule: AutomationSchedule, timeZone: string, now: Date) =>
  weeklyLocalScheduleToUtcCron(
    { ...schedule, repeat: 'week', weekdays: [...LOCAL_WEEKDAYS_MONDAY_THROUGH_FRIDAY] },
    timeZone,
    now,
  );

/** API stores UTC five-field crons; pass `browserTimeZone()` as `timeZone` for display and editing. */
export const automationScheduleToCron = ({
  now,
  schedule,
  timeZone,
}: {
  now: Date;
  schedule: AutomationSchedule;
  timeZone: string;
}) => {
  if (schedule.repeat === 'minute') {
    return '* * * * *';
  }
  if (schedule.repeat === '5min') {
    return '*/5 * * * *';
  }
  if (schedule.repeat === '15min') {
    return '*/15 * * * *';
  }
  if (schedule.repeat === '30min') {
    return '*/30 * * * *';
  }
  if (schedule.repeat === 'hour') {
    return '0 * * * *';
  }
  if (schedule.repeat === 'week') {
    return weeklyLocalScheduleToUtcCron(schedule, timeZone, now);
  }
  if (schedule.repeat === 'weekdays') {
    return weekdaysLocalScheduleToUtcCron(schedule, timeZone, now);
  }

  const today = readZonedParts(now, timeZone);
  const searchLength = schedule.repeat === 'month' ? MONTHS_TO_SEARCH : DAYS_TO_SEARCH;
  const days =
    schedule.repeat === 'month'
      ? Array.from({ length: searchLength }, (_, index) => {
          const monthIndex = today.month - 1 + index;
          const year = today.year + Math.floor(monthIndex / MONTHS_IN_YEAR);
          const month = (monthIndex % MONTHS_IN_YEAR) + 1;
          const probe = zonedDateTimeToUtc(
            { day: 1, hour: HOURS_ON_CLOCK, minute: 0, month, weekday: 0, year },
            timeZone,
          );
          const probed = readZonedParts(new Date(probe.getTime() + (schedule.dayOfMonth - 1) * ONE_DAY), timeZone);

          return probed;
        })
      : upcomingLocalDays(today, timeZone, searchLength);

  const instant = days
    .map((day) => ({ day, instant: localInstant(day, schedule, timeZone) }))
    .find(
      (candidate) =>
        candidate.instant.getTime() > now.getTime() &&
        matchesLocalSchedule(candidate.day, candidate.instant, schedule, timeZone),
    )?.instant;

  if (!instant) {
    return null;
  }

  if (schedule.repeat === 'month') {
    return `${instant.getUTCMinutes()} ${instant.getUTCHours()} ${instant.getUTCDate()} * *`;
  }

  return `${instant.getUTCMinutes()} ${instant.getUTCHours()} * * *`;
};

export const formatAutomationScheduleSentence = ({
  cronSchedule,
  now,
  timeZone,
}: {
  cronSchedule: string;
  now: Date;
  timeZone: string;
}) => {
  const schedule = parseAutomationSchedule({ cronSchedule, now, timeZone });

  if (!schedule) {
    return cronSchedule.trim();
  }
  if (schedule.repeat === 'minute') {
    return 'Every minute';
  }
  if (schedule.repeat === '5min') {
    return 'Every 5 minutes';
  }
  if (schedule.repeat === '15min') {
    return 'Every 15 minutes';
  }
  if (schedule.repeat === '30min') {
    return 'Every 30 minutes';
  }
  if (schedule.repeat === 'hour') {
    return 'Every hour';
  }
  const clock = formatAutomationScheduleClock(schedule.hour, schedule.minute);
  const zone = timeZoneAbbreviation(scheduleTimeZone(cronSchedule, timeZone), now);

  if (schedule.repeat === 'day') {
    return `Every day at ${clock} (${zone})`;
  }
  if (schedule.repeat === 'weekdays') {
    return `Every weekday at ${clock} (${zone})`;
  }
  if (schedule.repeat === 'week') {
    return `Every ${formatAutomationWeekdaysLabel(schedule.weekdays)} at ${clock} (${zone})`;
  }

  return `Every month on the ${schedule.dayOfMonth}${formatAutomationDayOrdinalSuffix(schedule.dayOfMonth)} at ${clock} (${zone})`;
};

export const formatAutomationNextRun = ({
  cronSchedule,
  now,
  timeZone,
}: {
  cronSchedule: string;
  now: Date;
  timeZone: string;
}) => {
  const zoned = getAutomationNextRunZoned({ cronSchedule, now, timeZone });

  if (!zoned) {
    return 'Next run —';
  }

  return `Next run ${formatAppointment(zoned)}`;
};

export const formatAutomationLastRun = ({
  lastRunAt,
  now,
  timeZone,
}: {
  lastRunAt: string | null | undefined;
  now: Date;
  timeZone: string;
}) => {
  if (!lastRunAt) {
    return 'Last run —';
  }
  const when = readZonedParts(new Date(lastRunAt), timeZone);
  const today = readZonedParts(now, timeZone);
  const clock = formatAutomationScheduleClock(when.hour, when.minute);
  const isToday = when.year === today.year && when.month === today.month && when.day === today.day;

  if (isToday) {
    return `Last run Today, ${clock}`;
  }

  return `Last run ${WEEKDAY_SHORT[when.weekday]}, ${MONTH_SHORT[when.month - 1]} ${when.day} at ${clock}`;
};

const calendarDayUtc = (parts: { year: number; month: number; day: number }) =>
  Date.UTC(parts.year, parts.month - 1, parts.day);

export const formatAutomationRunTimestamp = ({ at, now, timeZone }: { at: string; now: Date; timeZone: string }) => {
  const when = readZonedParts(new Date(at), timeZone);
  const today = readZonedParts(now, timeZone);
  const clock = formatAutomationScheduleClock(when.hour, when.minute);
  const whenDay = calendarDayUtc(when);
  const todayDay = calendarDayUtc(today);

  if (whenDay === todayDay) {
    return `Today, ${clock}`;
  }
  if (whenDay === todayDay - ONE_DAY) {
    return `Yesterday, ${clock}`;
  }

  return `${WEEKDAY_SHORT[when.weekday]}, ${MONTH_SHORT[when.month - 1]} ${when.day}, ${clock}`;
};

export const DEFAULT_TRIGGER_WEEKDAY = 5;

const nearestWeeklyPrefillTime = (zoned: ZonedDateTime): { hour: number; weekday: number } => {
  const roundedHour = Math.round(zoned.hour + zoned.minute / MINUTES_PER_HOUR);

  if (roundedHour >= HOURS_PER_DAY) {
    return {
      hour: 0,
      weekday: (zoned.weekday + 1) % DAYS_PER_WEEK,
    };
  }

  return {
    hour: roundedHour,
    weekday: zoned.weekday,
  };
};

/** Default weekly schedule when automating an organization skill (today's weekday, nearest on-the-hour time). */
export const buildWeeklyAutomationSchedulePrefillFromNow = ({
  now,
  timeZone,
}: {
  now: Date;
  timeZone: string;
}): AutomationSchedule => {
  const zoned = readZonedParts(now, timeZone);
  const { hour, weekday } = nearestWeeklyPrefillTime(zoned);

  return {
    dayOfMonth: 1,
    hour,
    minute: 0,
    repeat: 'week',
    weekdays: [weekday],
  };
};

export const TRIGGER_FORM_REPEAT: AutomationRepeat = 'week';

export const TRIGGER_FORM_REPEAT_OPTIONS: { label: string; value: AutomationRepeat }[] = [
  { label: 'every minute', value: 'minute' },
  { label: 'every 5 minutes', value: '5min' },
  { label: 'every 15 minutes', value: '15min' },
  { label: 'every 30 minutes', value: '30min' },
  { label: 'every hour', value: 'hour' },
  { label: 'every day', value: 'day' },
  { label: 'every weekday', value: 'weekdays' },
  { label: 'every week', value: 'week' },
  { label: 'every month', value: 'month' },
];

/** Normalize schedules for the trigger form (wall-clock fields, no illegal repeat combos). */
export const coerceAutomationScheduleForTriggerForm = ({
  now,
  schedule,
  timeZone,
}: {
  now: Date;
  schedule: AutomationSchedule;
  timeZone: string;
}): AutomationSchedule => {
  const zoned = readZonedParts(now, timeZone);

  if (
    schedule.repeat === 'minute' ||
    schedule.repeat === '5min' ||
    schedule.repeat === '15min' ||
    schedule.repeat === '30min'
  ) {
    return { ...schedule, weekdays: emptyWeekdays() };
  }
  if (schedule.repeat === 'hour') {
    return { ...schedule, minute: 0, weekdays: emptyWeekdays() };
  }
  if (schedule.repeat === 'weekdays') {
    return { ...schedule, minute: 0, weekdays: emptyWeekdays() };
  }
  if (schedule.repeat === 'day') {
    return { ...schedule, minute: 0, weekdays: emptyWeekdays() };
  }
  if (schedule.repeat === 'week') {
    const weekdays = normalizeAutomationWeekdays(schedule.weekdays);

    return {
      ...schedule,
      minute: 0,
      weekdays: weekdays.length > 0 ? weekdays : [DEFAULT_TRIGGER_WEEKDAY],
    };
  }

  return {
    ...schedule,
    dayOfMonth: schedule.dayOfMonth >= 1 && schedule.dayOfMonth <= DAY_FIELD_MAX ? schedule.dayOfMonth : zoned.day,
    minute: 0,
  };
};

export const triggerRepeatSelectValue = (schedule: AutomationSchedule | null): AutomationRepeat =>
  schedule?.repeat ?? TRIGGER_FORM_REPEAT;

export const automationTriggerDayOfMonthOptions = () =>
  Array.from({ length: DAY_FIELD_MAX }, (_, index) => {
    const day = index + 1;

    return { label: `${day}${formatAutomationDayOrdinalSuffix(day)}`, value: String(day) };
  });

/** Trigger form (Strato): on-the-hour times only. */
export const automationTriggerAtOptions = (_hour: number, _minute: number) =>
  Array.from({ length: HOURS_PER_DAY }, (_, index) => ({
    label: formatAutomationTime12h(index, 0),
    value: String(index * MINUTES_PER_HOUR),
  }));

export const formatAutomationCreatedDate = (createdAt: string, timeZone: string) => {
  const when = readZonedParts(new Date(createdAt), timeZone);

  return `${MONTH_SHORT[when.month - 1]} ${when.day}, ${when.year}`;
};

export const formatAutomationWeekdaysLabel = (weekdays: number[]) => {
  const days = normalizeAutomationWeekdays(weekdays);

  if (days.length === 0) {
    return '';
  }
  if (days.length === 1) {
    const day = days[0];

    return day === undefined ? '' : WEEKDAY_NAMES[day];
  }

  const shorts = days.map((day) => WEEKDAY_SHORT[day]);

  if (shorts.length === 2) {
    return `${shorts[0]} and ${shorts[1]}`;
  }

  return `${shorts.slice(0, -1).join(', ')} and ${shorts[shorts.length - 1]}`;
};

export const AUTOMATION_WEEKDAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const;

/** Monday-first index (0 = Monday) for trigger day pills; maps to JS weekday (0 = Sunday). */
export const automationWeekdayFromMondayFirstIndex = (mondayFirstIndex: number) =>
  (mondayFirstIndex + 1) % DAYS_PER_WEEK;
